/**
 * reminderOptions.ts — the "Remind me" picker's one list, client-side.
 *
 * Extracted from SchedulePreview (#1957) so the Add-event sheet offers exactly
 * the choices the Schedule Import card does. Two surfaces reading one array is
 * the point: the alternative is a second list that starts identical and drifts
 * the first time one of them gains an option.
 *
 * ⚠️ THIS UNION EXISTS IN THREE MORE PLACES, and they are NOT importable from
 * here — they run in Deno, not the browser:
 *
 *   1. src/lib/appleIcsHandoff.ts      → AppleReminderValue (imports this one)
 *   2. supabase/functions/_shared/icsBuilder.ts  → ReminderValue
 *   3. supabase/functions/schedule-ics/index.ts  → parseReminder's `allowed`
 *
 * (1) does import from here, so it cannot drift. (2) and (3) are hand-kept
 * copies across a runtime boundary, and (3) is the dangerous one: it is an
 * ALLOWLIST that silently falls back to "none". Widen this list without
 * widening that one and she picks "1 week before", the .ics is built with no
 * alarm at all, and nothing anywhere reports a problem.
 * `src/lib/__tests__/reminderOptionParity.test.ts` fails when the four disagree.
 */

/** Minutes before the event, as a string, or "none". */
export type ReminderValue = "none" | "0" | "15" | "30" | "60" | "1440" | "4320" | "10080";

export interface ReminderOption {
  value: ReminderValue;
  label: string;
}

export const REMINDER_OPTIONS: ReminderOption[] = [
  { value: "none", label: "None" },
  { value: "0", label: "At time of event" },
  { value: "15", label: "15 min before" },
  { value: "30", label: "30 min before" },
  { value: "60", label: "1 hour before" },
  { value: "1440", label: "1 day before" },
  // #1957 item 4. Both are whole days, so reminderToIcsDuration emits -P3D /
  // -P7D with no change needed there — it is generic over minutes.
  { value: "4320", label: "3 days before" },
  { value: "10080", label: "1 week before" },
];

/** Minutes for a picked value, or null for "none" / anything unparseable. */
export function reminderMinutesOf(value: ReminderValue): number | null {
  if (value === "none") return null;
  const mins = parseInt(value, 10);
  return Number.isFinite(mins) ? mins : null;
}

/**
 * The inverse: a stored `reminder_minutes` → the picker value that shows it.
 *
 * #1966 — needed by the ✏️ popover, which has to DISPLAY what is already set.
 * A picker that always opened on "None" would make every time edit a silent
 * wipe of her reminder.
 *
 * ⚠️ Returns null — not "none" — for a number this list cannot express. Those
 * are different answers and the caller must not conflate them: "none" means
 * she chose no reminder, null means "there is a value here I cannot render".
 * The ✏️ popover only writes when she actually touches the control, so an
 * unrepresentable value is displayed wrongly but never destroyed. Collapsing
 * this to "none" would turn a reminder Kin cannot draw into one it deletes.
 */
export function reminderValueOf(minutes: number | null | undefined): ReminderValue | null {
  if (minutes == null) return "none";
  const match = REMINDER_OPTIONS.find((o) => o.value !== "none" && reminderMinutesOf(o.value) === minutes);
  return match ? match.value : null;
}
